import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';

const inputClass =
  'w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition bg-white';
const labelClass = 'block text-sm font-semibold text-slate-700 mb-1.5';
const requiredMark = <span className="text-orange-500">*</span>;

const TURNSTILE_SITE_KEY = '0x4AAAAAACvvXFwuMpQSvwkG';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          language?: string;
        }
      ) => string | number;
      reset: (widgetId?: string | number) => void;
      remove?: (widgetId: string | number) => void;
    };
  }
}

const facilityTypes = [
  '介護施設',
  'デイサービス',
  '歯科医院',
  '医療機関',
  '訪問介護・訪問看護事業所',
  '障害福祉施設',
  'その他',
];

const mediaOptions = [
  '施設内での口腔体操',
  'WEBサイト',
  'SNS',
  'パンフレット・チラシ',
  '施設内資料・掲示物',
  'DVD・CD・ダウンロード可能なデータ',
  '音楽・カラオケ',
  'その他',
];

export default function ApplicationForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadyUsing, setAlreadyUsing] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const renderTurnstile = () => {
      if (
        cancelled ||
        !window.turnstile ||
        !turnstileContainerRef.current ||
        turnstileWidgetIdRef.current !== null
      ) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
          language: 'ja',
          callback: (token: string) => {
            setTurnstileToken(token);
            setError(null);
          },
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => {
            setTurnstileToken('');
            setError('ロボット確認の読み込みに失敗しました。ページを再読み込みしてください。');
          },
        }
      );
    };

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-patakara-turnstile="true"]'
    );

    if (existingScript) {
      if (window.turnstile) {
        renderTurnstile();
      } else {
        existingScript.addEventListener('load', renderTurnstile, { once: true });
      }
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.setAttribute('data-patakara-turnstile', 'true');
      script.addEventListener('load', renderTurnstile, { once: true });
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (turnstileWidgetIdRef.current !== null && window.turnstile?.remove) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      }
      turnstileWidgetIdRef.current = null;
    };
  }, []);


  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;

    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const fd = new FormData(form);
    const str = (v: unknown) => (v ? String(v).trim() : '');

    const email = str(fd.get('email'));
    const emailConfirm = str(fd.get('email_confirm'));
    if (email !== emailConfirm) {
      setError('メールアドレスが一致しません。もう一度ご確認ください。');
      return;
    }

    const requiredChecks = [
      'confirm_accuracy',
      'confirm_guidelines',
      'confirm_privacy',
    ];
    const missingChecks = requiredChecks.filter((n) => fd.get(n) !== 'on');
    if (missingChecks.length > 0) {
      setError('確認事項のすべてにチェックを入れてください。');
      return;
    }

    const media = fd.getAll('media') as string[];
    if (media.length === 0) {
      setError('利用予定の媒体を1つ以上選択してください。');
      return;
    }

    if (!turnstileToken) {
      setError('「私はロボットではありません」の確認を完了してください。');
      return;
    }

    fd.set('cf-turnstile-response', turnstileToken);

    setSubmitting(true);

    try {
      // 自社サーバーの apply.php へ送信
      const response = await fetch('/apply.php', {
        method: 'POST',
        body: fd,
      });

      const parsed = await response.json();

      if (response.ok && parsed?.ok) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(parsed?.message || '送信に失敗しました。時間をおいて再度お試しください。');
        setTurnstileToken('');
        if (turnstileWidgetIdRef.current !== null) {
          window.turnstile?.reset(turnstileWidgetIdRef.current);
        }
      }
    } catch {
      setError('送信に失敗しました。時間をおいて再度お試しください。');
      setTurnstileToken('');
      if (turnstileWidgetIdRef.current !== null) {
        window.turnstile?.reset(turnstileWidgetIdRef.current);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-16 animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <i className="ri-check-line text-green-500 text-4xl"></i>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-6">事前申込みを受け付けました</h2>
        <div className="max-w-xl mx-auto space-y-4 text-gray-700 leading-relaxed text-sm md:text-base">
          <p>お申込みありがとうございます。</p>
          <p>施設・事業所の情報および利用内容を確認後、担当者よりメールでご連絡します。</p>
          <p>この時点では、公式施設ライセンスの契約は成立していません。</p>
        </div>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-8 py-3 bg-orange-500 text-white font-semibold rounded-full hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer mt-10"
        >
          <i className="ri-home-line"></i>
          トップページへ戻る
        </Link>
      </div>
    );
  }

  return (
    <form
      id="license-application-form"
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-10"
    >
      {/* 迷惑送信対策（非表示フィールド） */}
      <input
        type="text"
        name="company_alt"
        className="supplemental-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {/* 申込者情報 */}
      <section>
        <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">1</span>
          申込者情報
        </h3>
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>法人名</label>
              <input type="text" name="company_name" className={inputClass} placeholder="例：株式会社〇〇" />
            </div>
            <div>
              <label className={labelClass}>施設名・事業所名 {requiredMark}</label>
              <input type="text" name="facility_name" required className={inputClass} placeholder="例：〇〇介護施設" />
            </div>
          </div>

          <div>
            <label className={labelClass}>施設種別 {requiredMark}</label>
            <select name="facility_type" required className={`${inputClass} cursor-pointer`} defaultValue="">
              <option value="" disabled>選択してください</option>
              {facilityTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>郵便番号 {requiredMark}</label>
              <input type="text" name="postal_code" required className={inputClass} placeholder="例：231-0023" />
            </div>
            <div>
              <label className={labelClass}>公式WEBサイトのURL</label>
              <input type="text" name="website_url" className={inputClass} placeholder="例：https://example.com" />
            </div>
          </div>

          <div>
            <label className={labelClass}>所在地 {requiredMark}</label>
            <input type="text" name="address" required className={inputClass} placeholder="例：神奈川県横浜市中区〇〇1-2-3" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>担当者名 {requiredMark}</label>
              <input type="text" name="contact_name" required className={inputClass} placeholder="例：山田 太郎" />
            </div>
            <div>
              <label className={labelClass}>担当部署・役職</label>
              <input type="text" name="department" className={inputClass} placeholder="例：施設長・管理者" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>電話番号 {requiredMark}</label>
              <input type="tel" name="phone" required className={inputClass} placeholder="例：045-000-0000" />
            </div>
            <div>
              <label className={labelClass}>メールアドレス {requiredMark}</label>
              <input type="email" name="email" required className={inputClass} placeholder="例：info@example.com" />
            </div>
          </div>

          <div>
            <label className={labelClass}>メールアドレス（確認） {requiredMark}</label>
            <input type="email" name="email_confirm" required className={inputClass} placeholder="メールアドレスをもう一度入力" />
          </div>
        </div>
      </section>

      {/* 利用内容 */}
      <section className="mt-10 pt-8 border-t border-gray-100">
        <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">2</span>
          利用内容
        </h3>
        <div className="space-y-5">
          <div>
            <label className={labelClass}>「パタカラ®」の利用目的 {requiredMark}</label>
            <textarea name="purpose" required rows={3} maxLength={500} className={`${inputClass} resize-none`} placeholder="例：利用者向けの口腔機能向上プログラムとして実施するため" />
          </div>

          <div>
            <label className={labelClass}>実施予定の内容 {requiredMark}</label>
            <textarea name="planned_content" required rows={4} maxLength={500} className={`${inputClass} resize-none`} placeholder="実施予定のプログラムやサービスの具体的な内容をご記入ください" />
          </div>

          <div>
            <label className={labelClass}>利用予定の媒体（複数選択可） {requiredMark}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {mediaOptions.map((m) => (
                <label key={m} className="flex items-center gap-3 px-3 py-2.5 border border-gray-200 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors">
                  <input type="checkbox" name="media" value={m} className="w-4 h-4 text-orange-500 rounded cursor-pointer" />
                  <span className="text-sm text-gray-700">{m}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>現在すでに「パタカラ」を使用しているか {requiredMark}</label>
            <select
              name="already_using"
              required
              className={`${inputClass} cursor-pointer`}
              defaultValue=""
              onChange={(e) => setAlreadyUsing(e.target.value === 'すでに使用している')}
            >
              <option value="" disabled>選択してください</option>
              <option value="使用していない">使用していない</option>
              <option value="すでに使用している">すでに使用している</option>
            </select>
          </div>

          {alreadyUsing && (
            <div>
              <label className={labelClass}>使用中の場合の掲載URLまたは詳細</label>
              <textarea name="usage_url" rows={3} maxLength={500} className={`${inputClass} resize-none`} placeholder="掲載されているWEBサイトのURLや使用状況の詳細をご記入ください" />
            </div>
          )}

          <div>
            <label className={labelClass}>その他の相談事項</label>
            <textarea name="other_notes" rows={4} maxLength={500} className={`${inputClass} resize-none`} placeholder="ご不明な点やご相談事項がございましたらご記入ください" />
          </div>
        </div>
      </section>

      {/* 確認事項 */}
      <section className="mt-10 pt-8 border-t border-gray-100">
        <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">3</span>
          確認事項
        </h3>
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" name="confirm_accuracy" className="w-4 h-4 text-orange-500 rounded cursor-pointer mt-0.5" />
            <span className="text-sm text-gray-700 leading-relaxed">入力した内容に相違ありません。</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" name="confirm_guidelines" className="w-4 h-4 text-orange-500 rounded cursor-pointer mt-0.5" />
            <span className="text-sm text-gray-700 leading-relaxed">
              公式施設ライセンスの利用条件および
              <Link to="/trademark-guidelines" className="text-orange-600 underline hover:text-orange-700">商標利用ガイドライン</Link>
              を確認しました。
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" name="confirm_privacy" className="w-4 h-4 text-orange-500 rounded cursor-pointer mt-0.5" />
            <span className="text-sm text-gray-700 leading-relaxed">
              <Link to="/terms" className="text-orange-600 underline hover:text-orange-700">プライバシーポリシー</Link>
              に同意します。
            </span>
          </label>
        </div>
      </section>

      {/* Cloudflare Turnstile */}
      <div className="mt-8 flex justify-center">
        <div ref={turnstileContainerRef} />
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 leading-relaxed">
          {error}
        </div>
      )}

      {/* 送信ボタン */}
      <button
        type="submit"
        disabled={submitting || !turnstileToken}
        className="w-full py-4 bg-orange-500 text-white font-bold text-base rounded-full hover:bg-orange-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 mt-8"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            送信しています…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-lg"></i>
            事前申込みを送信する
          </>
        )}
      </button>

      <p className="text-xs text-gray-400 text-center leading-relaxed mt-4">
        ご入力いただいた個人情報は、公式施設ライセンスの事前申込みに関する確認・ご案内のみに使用いたします。
      </p>
    </form>
  );
}